"use client";

import { useMemo } from "react";
import { isAddress } from "viem";
import { useAccount, useReadContract } from "wagmi";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import ContractAddress from "@/contract/addresses";

/**
 * Outcome of validating a candidate referrer (= binary-tree parent) address.
 *
 * The rule is taken verbatim from the contract so the UI can never disagree
 * with what the chain will actually do. `Users.addUserToList` rejects a parent
 * for exactly two reasons:
 *   - `ParentNotExist`                      → `parent.exists == false`
 *   - "parent can not have more than 2 dir" → `parent.bv.dir >= 2`
 * Everything else is a client-side pre-check (format / self-referral).
 */
export type ReferrerStatus =
  | "idle" // nothing typed yet — show no feedback
  | "invalid" // not a well-formed 0x address
  | "self" // the buyer's own wallet
  | "checking" // chain read in flight
  | "not-member" // exists === false: never completed a purchase
  | "full" // bv.dir >= 2: both legs already taken
  | "unreachable" // RPC failed — we genuinely do not know
  | "valid";

/** `Users.sol` allows at most two direct children per node. */
export const MAX_DIRECT_CHILDREN = 2n;

/**
 * Status → key under the `checkout.referrer` message namespace. Shared so the
 * inline field and the ticket page cannot drift apart in what they tell users.
 */
export const REFERRER_MESSAGE_KEY: Record<ReferrerStatus, string> = {
  idle: "requiredForFirstPurchase",
  invalid: "invalid",
  self: "self",
  checking: "checking",
  "not-member": "notMember",
  full: "full",
  unreachable: "unreachable",
  valid: "valid",
};

export interface UseReferrerValidationReturn {
  status: ReferrerStatus;
  /** True only when the chain confirms this address can accept a new child. */
  isValid: boolean;
  refetch: () => void;
}

/**
 * Live-validates a referrer wallet address against the DAO contract.
 *
 * Deliberately reads the chain rather than the database: the whole class of
 * misplacement bugs this replaces came from trusting off-chain state that had
 * drifted from (or was never bound to) the wallet actually signing the mint.
 *
 * No manual debounce is needed — the read only fires once the input parses as a
 * complete address, and wagmi/react-query dedupes and caches per address.
 */
export const useReferrerValidation = (
  rawAddress: string | undefined,
): UseReferrerValidationReturn => {
  const { address: buyer } = useAccount();
  const candidate = (rawAddress ?? "").trim();

  const isWellFormed = isAddress(candidate);
  const isSelf =
    isWellFormed && !!buyer && candidate.toLowerCase() === buyer.toLowerCase();

  const referrer = useReadContract({
    address: ContractAddress.GATEWAY,
    abi: GATEWAY_ABI,
    functionName: "getUser",
    args: [candidate as `0x${string}`],
    query: { enabled: isWellFormed && !isSelf },
  });

  const status = useMemo<ReferrerStatus>(() => {
    if (candidate.length === 0) return "idle";
    if (!isWellFormed) return "invalid";
    if (isSelf) return "self";
    if (referrer.isLoading) return "checking";
    if (referrer.isError) return "unreachable";
    const user = referrer.data;
    // `enabled` just flipped true and the first read has not resolved yet.
    if (!user) return "checking";
    if (!user.exists) return "not-member";
    if (user.bv.dir >= MAX_DIRECT_CHILDREN) return "full";
    return "valid";
  }, [
    candidate,
    isWellFormed,
    isSelf,
    referrer.isLoading,
    referrer.isError,
    referrer.data,
  ]);

  return {
    status,
    isValid: status === "valid",
    refetch: referrer.refetch,
  };
};
